import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import { 
  Clock, 
  Send, 
  Users, 
  Lightbulb,
  ArrowLeft,
  CheckCircle,
  Loader,
  Zap,
  Brain,
  Sparkles,
  Wand2,
  Target,
  Timer,
  Lock as LockIcon,
  Unlock as UnlockIcon,
  Maximize2
} from 'lucide-react'
import { supabase } from '@/config/supabase'
import { databaseService } from '@/services/database'
import { realtimeService } from '@/services/realtime'
import { runwareService } from '@/services/runware'
import { geminiService } from '@/services/gemini'
import { preferencesService } from '@/services/preferences'
import { authService } from '@/services/auth'
import { audioService } from '@/services/audio'
import { Room, Player, Prompt, UserLimits } from '@/types'
import { trackEvent } from '@/services/analytics'
import PaywallModal from '@/components/PaywallModal'
import ReportContentButton from '@/components/ReportContentButton'
import PromptLimitIndicator from '@/components/PromptLimitIndicator'
import FullscreenImageModal from '@/components/FullscreenImageModal'
import { useTranslation } from 'react-i18next'
import RoomClosedModal from '@/components/RoomClosedModal'

export default function GamePage() {
  const { roomId } = useParams<{ roomId: string }>()
  const navigate = useNavigate()
  const { t, i18n } = useTranslation()
  
  const [room, setRoom] = useState<Room | null>(null)
  const [currentPlayer, setCurrentPlayer] = useState<Player | null>(null)
  const [prompt, setPrompt] = useState('')
  const [submittedPrompt, setSubmittedPrompt] = useState<Prompt | null>(null)
  const [timeLeft, setTimeLeft] = useState(0)
  const [roundStartedAt, setRoundStartedAt] = useState<number | null>(null)
  const [loading, setLoading] = useState(true)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState('')
  const [allPrompts, setAllPrompts] = useState<Prompt[]>([])
  const [locking, setLocking] = useState(false)
  const [timeoutHandled, setTimeoutHandled] = useState(false)
  const [userLimits, setUserLimits] = useState<UserLimits | null>(null)
  const [showPaywall, setShowPaywall] = useState(false)
  const [timerWarningPlayed, setTimerWarningPlayed] = useState(false)
  const [nsfwBlockedPromptId, setNsfwBlockedPromptId] = useState<string | null>(null)
  const [nsfwRetryUsed, setNsfwRetryUsed] = useState(false)
  const [isNsfwRetryMode, setIsNsfwRetryMode] = useState(false)
  const [showNsfwModal, setShowNsfwModal] = useState(false)
  const [nsfwModalMessage, setNsfwModalMessage] = useState<string>('')
  const [showRoomClosed, setShowRoomClosed] = useState(false)
  const [showFullscreenImage, setShowFullscreenImage] = useState(false)

  const amIHost = () => {
    try {
      if (currentPlayer && typeof currentPlayer.isHost === 'boolean') return !!currentPlayer.isHost
      const saved = localStorage.getItem('currentPlayer')
      if (!saved) return false
      const me = JSON.parse(saved)
      return !!me?.isHost
    } catch {
      return !!currentPlayer?.isHost
    }
  }

  useEffect(() => {
    if (!roomId) {
      navigate('/')
      return
    }

    // Get current player from localStorage
    const savedPlayer = localStorage.getItem('currentPlayer')
    if (savedPlayer) {
      setCurrentPlayer(JSON.parse(savedPlayer))
    }

    loadRoom()
    const cleanupRealtime = setupRealtimeSubscription()
    setupRealtimeBroadcasts()
    
    // Cleanup function
    return () => {
      if (cleanupRealtime) {
        cleanupRealtime()
      }
      // Note: Don't cleanup room here as it causes issues with rapid page transitions
      // Cleanup will happen when user actually leaves the room via buttons
    }
  }, [roomId])
  // Host presence kontrolü: host ayrılırsa modalı aç
  useEffect(() => {
    if (!roomId) return
    const playerJson = localStorage.getItem('currentPlayer')
    const me: Player | null = playerJson ? JSON.parse(playerJson) : null
    if (!me) return

    realtimeService.onPresenceSync(roomId, () => {
      // Host offline olsa bile modal açma davranışını kaldırdık
    })
  }, [roomId, room?.players])

  // Presence kanalı: odaya katıl (host dahil)
  useEffect(() => {
    if (!roomId || !currentPlayer) return
    const isHost = !!room?.players.find(p => p.id === currentPlayer.id)?.isHost || !!currentPlayer.isHost
    const isReady = !!room?.players.find(p => p.id === currentPlayer.id)?.isReady || !!currentPlayer.isReady
    realtimeService.joinRoom(roomId, {
      id: currentPlayer.id,
      name: currentPlayer.name,
      isHost,
      isReady
    })

  }, [roomId, currentPlayer?.id, currentPlayer?.name, room?.players])

  // Load user limits
  useEffect(() => {
    const loadUserLimits = async () => {
      try {
        const limits = await authService.getUserLimits()
        setUserLimits(limits)
      } catch (error) {
        console.error('Error loading user limits:', error)
      }
    }

    loadUserLimits()
  }, [])

  // Reset NSFW retry state when prompt successfully completes
  useEffect(() => {
    if (isNsfwRetryMode && submittedPrompt && submittedPrompt.id === nsfwBlockedPromptId && submittedPrompt.imageGenerationStatus === 'completed') {
      setIsNsfwRetryMode(false)
      setNsfwBlockedPromptId(null)
      setNsfwRetryUsed(false)
      setShowNsfwModal(false)
      setNsfwModalMessage('')
    }
  }, [isNsfwRetryMode, submittedPrompt?.id, submittedPrompt?.imageGenerationStatus, nsfwBlockedPromptId])

  // Server-side timer effect
  useEffect(() => {
    if (!room || room.status !== 'prompt_writing') return

    const updateTimeFromServer = async () => {
      try {
        const { timeLeft, isExpired } = await databaseService.getRemainingTime(room.id)
        setTimeLeft(timeLeft)

        // Play warning sound when exactly 10 seconds left (only once per round)
        if (timeLeft === 10 && !timerWarningPlayed) {
          setTimerWarningPlayed(true)
          audioService.playTimerWarning()
        }

        if (isExpired && !timeoutHandled) {
          // Server says time is expired, handle timeout (only once)
          if (currentPlayer) {
            try {
              setTimeoutHandled(true)
              console.log('Time expired, finalizing round on timeout')
              await databaseService.finalizeRoundOnTimeout(room.id, room.roundNumber)
              
              // Wait for database triggers to complete, then reload
              setTimeout(() => {
                console.log('Reloading room after timeout handling')
                loadRoom()
              }, 2000) // Increased delay to ensure database operations complete
            } catch (error) {
              console.error('Error handling timeout:', error)
              setTimeoutHandled(false) // Reset flag on error
            }
          }
        }
      } catch (error) {
        console.error('Error getting server time:', error)
      }
    }

    // Update immediately
    updateTimeFromServer()

    // Then update every second
    const interval = setInterval(updateTimeFromServer, 1000)

    return () => clearInterval(interval)
  }, [room?.id, room?.status, currentPlayer?.id, room?.roundNumber, timeoutHandled])

  // Database will handle automatic progression, we just listen for changes
  useEffect(() => {
    if (room && room.status === 'voting') {
      // Database automatically moved to voting, navigate
      console.log('Room status changed to voting, navigating to voting page')
      navigate(`/voting/${roomId}`)
    }
  }, [room?.status, roomId, navigate])

  // Redirect to user word submission page if we're in the new mode and still waiting for words
  useEffect(() => {
    if (room?.gameMode === 'user_word_to_prompt' && room?.status === 'waiting') {
      navigate(`/userword/${roomId}`)
    }
  }, [room?.gameMode, room?.status, roomId, navigate])

  // Safety: if in user_word_to_prompt and status is prompt_writing but currentWord is missing, redirect to word submission
  useEffect(() => {
    if (
      room?.gameMode === 'user_word_to_prompt' &&
      room?.status === 'prompt_writing' &&
      (!room.currentWord || String(room.currentWord).trim() === '')
    ) {
      navigate(`/userword/${roomId}`)
    }
  }, [room?.gameMode, room?.status, room?.currentWord, roomId, navigate])

  // Remove the extra force-sync effect as it causes conflicts with timeout handling
  // The main timer effect already handles expiry appropriately

  const loadRoom = async () => {
    try {
      const roomData = await databaseService.getRoom(roomId!)
      setRoom(roomData)

      // Host ayrıldıysa/oda kapandıysa modal göster
      if (roomData.status === 'finished') {
        setShowRoomClosed(true)
        return
      }
      
      // Reset timeout flag when room status changes
      if (roomData.status !== room?.status) {
        setTimeoutHandled(false)
      }
      
      // Reset timer warning flag when round changes or status changes
      if (roomData.roundNumber !== room?.roundNumber || roomData.status !== room?.status) {
        setTimerWarningPlayed(false)
      }
      
      if (roomData.status === 'voting') {
        navigate(`/voting/${roomId}`)
        return
      }
      
      if (roomData.status === 'results') {
        navigate(`/results/${roomId}`)
        return
      }

      // Load current round prompts
      const prompts = await databaseService.getRoundPrompts(roomId!, roomData.roundNumber)
      console.log('Loaded prompts in loadRoom:', prompts)
      setAllPrompts(prompts)

      // If in user_word_to_prompt and there are no prompts yet right after switching to prompt_writing,
      // allow a short grace period before showing empty state
      if (roomData.gameMode === 'user_word_to_prompt' && roomData.status === 'prompt_writing' && prompts.length === 0) {
        // Trigger a quick re-fetch after a short delay to capture late inserts
        setTimeout(async () => {
          try {
            const refreshed = await databaseService.getRoundPrompts(roomId!, roomData.roundNumber)
            if (refreshed.length > 0) {
              setAllPrompts(refreshed)
            }
          } catch {}
        }, 800)
      }

      // Check if current player already submitted for current round
      if (currentPlayer) {
        const userPrompt = prompts.find(p => p.playerId === currentPlayer.id && p.roundNumber === roomData.roundNumber)
        console.log('Current user prompt:', userPrompt)
        setSubmittedPrompt(userPrompt || null)
        // Clear prompt input if no submission for current round
        if (!userPrompt) {
          setPrompt('')
        }
      } else {
        setSubmittedPrompt(null)
        setPrompt('')
      }

      // If roundStartedAt is not set and game is active, set it to now
      // This handles page refresh scenarios, but don't override if already set from broadcast
      if (roomData.status === 'prompt_writing' && !roundStartedAt) {
        console.log('Setting roundStartedAt to now for page refresh scenario')
        setRoundStartedAt(Date.now())
      }
    } catch (error: any) {
      setError(error.message || 'Oda yüklenirken hata oluştu')
    } finally {
      setLoading(false)
    }
  }

  const setupRealtimeSubscription = () => {
    const channel = supabase
      .channel(`game-${roomId}`)
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'rooms',
        filter: `id=eq.${roomId}`
      }, (payload) => {
        console.log('Room changed:', payload)
        const ev: any = (payload as any)
        const dbRoom: any = ev.new
        if (ev.eventType === 'DELETE') {
          if (!amIHost()) setShowRoomClosed(true)
          return
        }
        if (dbRoom && dbRoom.id === roomId) {
          if (dbRoom.status === 'finished') {
            if (!amIHost()) setShowRoomClosed(true)
            return
          }
          // Immediately handle status changes
          if (dbRoom.status === 'voting') {
            console.log('Room status changed to voting via realtime, navigating immediately')
            navigate(`/voting/${roomId}`)
            return
          }
          
          // Quick sync for other changes
          setRoom((prev) => {
            if (!prev) return prev
            return {
              ...prev,
              status: dbRoom.status ?? prev.status,
              currentWord: dbRoom.current_word ?? prev.currentWord,
              currentWordTr: (dbRoom as any).current_word_tr ?? prev.currentWordTr,
              currentWordEn: (dbRoom as any).current_word_en ?? prev.currentWordEn,
              currentVotingPromptId: dbRoom.current_voting_prompt_id ?? prev.currentVotingPromptId,
              roundStartedAt: dbRoom.round_started_at ?? prev.roundStartedAt,
              votingStartedAt: dbRoom.voting_started_at ?? prev.votingStartedAt,
              maxPlayers: dbRoom.max_players ?? prev.maxPlayers,
              roundNumber: dbRoom.round_number ?? prev.roundNumber,
              maxRounds: dbRoom.max_rounds ?? prev.maxRounds,
            }
          })
          
          // Reload for complete sync after a small delay
          setTimeout(() => {
            loadRoom()
          }, 500)
        }
      })
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'players',
        filter: `room_id=eq.${roomId}`
      }, (payload) => {
        try {
          const ev: any = payload as any
          const wasDelete = ev.eventType === 'DELETE'
          const oldRow = (ev.old || {}) as any
          if (wasDelete && oldRow && oldRow.is_host && !currentPlayer?.isHost) {
            setShowRoomClosed(true)
            return
          }
        } catch {}
      })
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'prompts',
        filter: `room_id=eq.${roomId}`
      }, async (payload) => {
        console.log('Prompt changed:', payload)
        if (!roomId) return
        try {
          // Quick room status check first
          const latestRoom = await databaseService.getRoom(roomId)
          if (latestRoom.status === 'voting') {
            console.log('Room moved to voting after prompt change')
            navigate(`/voting/${roomId}`)
            return
          }
          
          // Update state
          setRoom(latestRoom)
          const prompts = await databaseService.getRoundPrompts(roomId, latestRoom.roundNumber)
          console.log('Updated prompts from realtime:', prompts)
          setAllPrompts(prompts)
          
          // Also update submittedPrompt if it's the current user's prompt
          if (currentPlayer) {
            const userPrompt = prompts.find(p => p.playerId === currentPlayer.id && p.roundNumber === latestRoom.roundNumber)
            if (userPrompt) {
              console.log('Updating submittedPrompt from realtime:', userPrompt)
              setSubmittedPrompt(userPrompt)
              
              // If this is a new prompt from current user and it doesn't have an image yet, generate it
              if (userPrompt.imageGenerationStatus === 'pending' && !userPrompt.imageUrl) {
                console.log('New prompt detected for current user, starting image generation')
                await generateImage(userPrompt)
              }
            }
          }
        } catch (e) {
          console.error('Error syncing prompts after change:', e)
        }
      })
      .subscribe()

    return () => {
      try {
        supabase.removeChannel(channel)
      } catch (error) {
        console.log('WebSocket cleanup completed')
      }
    }
  }



  const setupRealtimeBroadcasts = () => {
    if (!roomId) return
    realtimeService.on(roomId, 'round_started', (payload) => {
      if (!payload) return
      console.log('Received round_started broadcast:', payload)
      setRoundStartedAt(payload.startedAt)
      
      if (payload.phase === 'voting') {
        navigate(`/voting/${roomId}`)
        return
      }
      
      if (payload.phase === 'prompt_writing') {
        // New round started, reload room data after a small delay to ensure state is updated
        console.log('New prompt_writing round started, reloading room data')
        setTimeoutHandled(false) // Reset timeout flag for new round
        setTimeout(() => {
          loadRoom()
        }, 100) // Small delay to ensure roundStartedAt state is updated
        // Don't manually set timeLeft here, let the timer useEffect handle it
      }
    })
  }

  const submitPrompt = async () => {
    console.log('submitPrompt called with:', { currentPlayer: currentPlayer?.id, room: room?.id, prompt: prompt.trim() })
    
    if (!currentPlayer || !room || !prompt.trim()) {
      console.log('submitPrompt early return - missing data')
      return
    }

    // Check user prompt limit before proceeding (her iki modda da)
    try {
      const canCreate = await authService.canUserCreatePrompt()
      if (!canCreate) {
        setShowPaywall(true)
        return
      }
    } catch (error) {
      console.error('Error checking prompt limit:', error)
      setError(t('game.errors.promptLimitCheck'))
      return
    }

    // Validate prompt
    const validation = runwareService.validatePrompt(prompt)
    if (!validation.isValid) {
      console.log('Prompt validation failed:', validation.error)
      setError(validation.error || (t('game.errors.invalidPrompt') as string))
      return
    }

    console.log('Starting prompt submission process')
    setSubmitting(true)
    setError('')

    try {
      let promptData: Prompt
      if (isNsfwRetryMode && nsfwBlockedPromptId) {
        // Mevcut promptu düzenleyip tekrar dener
        promptData = await databaseService.updatePromptForRetry(nsfwBlockedPromptId, prompt.trim())
      } else {
        // Yeni prompt gönder
        console.log('Submitting prompt to database...')
        promptData = await databaseService.submitPrompt(
          currentPlayer.id,
          room.id,
          room.roundNumber,
          prompt.trim()
        )
      }

      console.log('Prompt submitted successfully:', promptData)
      setSubmittedPrompt(promptData)

      // Analytics: prompt gonderimi (sadece DB kaydi basariliysa)
      trackEvent({
        event: 'scraible_prompt_submit',
        properties: {
          room_id: room.id,
          round_number: room.roundNumber,
          game_mode: room.gameMode,
          prompt_id: promptData.id,
          nsfw_retry: isNsfwRetryMode,
        },
      })
      
      // Increment user prompt count (sadece ilk gönderimde)
      if (!(isNsfwRetryMode && nsfwBlockedPromptId)) {
        try {
          const updatedLimits = await authService.incrementUserPromptCount()
          setUserLimits(updatedLimits)
        } catch (error) {
          console.error('Error incrementing prompt count:', error)
        }
      }
      
      // Notify others quickly
      if (roomId) {
        console.log('Broadcasting prompt submission...')
        await realtimeService.broadcast(roomId, 'prompt_submitted', {
          playerId: currentPlayer.id,
          promptId: promptData.id,
        })
      }

      // Generate image (manuel tercih etkinse veya TR ise çeviri uygula)
      console.log('Starting image generation...')
      let promptForModel = promptData.text
      try {
        const { enabled } = preferencesService.getPromptTranslation()
        const currentLang = i18n.language || 'tr'
        const shouldTranslate = enabled
        if (shouldTranslate) {
          if (geminiService.isEnabled()) {
            // Her zaman İngilizceye çevir; kaynak ipucu olarak mevcut dili kullan
            promptForModel = await geminiService.translate(promptForModel, 'en', currentLang)
            console.log('Prompt translated via Gemini:', promptForModel)
          } else {
            console.warn('Gemini disabled or missing API key; sending original prompt')
          }
        }
      } catch (e) {
        console.warn('Gemini translation failed, falling back to original prompt', e)
      }

      const genResult = await runwareService.generateImage(promptForModel)
      if (genResult.NSFWContent) {
        console.warn('NSFW content detected. Handling according to retry policy.')
        if (!nsfwRetryUsed) {
          // İlk NSFW: engelle ve bir kez daha deneme hakkı ver
          await databaseService.updatePromptImage(promptData.id, '', 'failed', 'NSFW içerik tespit edildi')
          setAllPrompts(prev => prev.map(p => p.id === promptData.id ? { ...p, imageGenerationStatus: 'failed' } as any : p))
          setPrompt(promptData.text)
          setIsNsfwRetryMode(true)
          setNsfwBlockedPromptId(promptData.id)
          setNsfwModalMessage('Uygunsuz içerik nedeniyle engellendi. Promptu düzenleyip bir kez daha deneyebilirsin.')
          setShowNsfwModal(true)
        } else {
          // İkinci NSFW: boş URL ile completed ve kilitli işaretle, oyun ilerleyebilsin
          await databaseService.updatePromptImage(promptData.id, '', 'completed', 'NSFW içerik nedeniyle görsel üretilmedi')
          await databaseService.lockPrompt(promptData.id)
          setAllPrompts(prev => prev.map(p => p.id === promptData.id ? { ...p, imageUrl: '', imageGenerationStatus: 'completed', isLocked: true } as any : p))
          setIsNsfwRetryMode(false)
          setNsfwBlockedPromptId(null)
          setNsfwModalMessage('Uygunsuz içerik nedeniyle engellendi. Tekrar deneme hakkın tükendi.')
          setShowNsfwModal(true)
        }
        return
      }
      if (genResult.imageURL) {
        await databaseService.updatePromptImage(promptData.id, genResult.imageURL, 'completed')
        await loadRoom()
        setAllPrompts(prev => prev.map(p => p.id === promptData.id ? { ...p, imageUrl: genResult.imageURL, imageGenerationStatus: 'completed' } as any : p))
        // Clear NSFW retry state on success
        if (isNsfwRetryMode) {
          setIsNsfwRetryMode(false)
          setNsfwBlockedPromptId(null)
          setNsfwRetryUsed(false)
          setShowNsfwModal(false)
          setNsfwModalMessage('')
        }
      }
    } catch (error: any) {
      console.error('Error in submitPrompt:', error)
      setError(error.message || (t('game.errors.loadRoom') as string))
    } finally {
      setSubmitting(false)
    }
  }

  const generateImage = async (promptData: Prompt) => {
    try {
      const isEnglish = i18n.language?.startsWith('en')
      const localizedWord = isEnglish
        ? (room?.currentWordEn || room?.currentWord || '')
        : (room?.currentWordTr || room?.currentWord || '')
      if (!localizedWord) return

      console.log('Starting image generation for prompt:', promptData.id)

      // Mark generating locally to prevent duplicate clicks
      setAllPrompts(prev => prev.map(p => p.id === promptData.id ? { ...p, imageGenerationStatus: 'generating' } as any : p))

      // Enhance prompt with the word
      const enhancedPrompt = runwareService.enhancePrompt(promptData.text, localizedWord)
      
      // Generate image
      const response = await runwareService.generateImage(enhancedPrompt)
      
      console.log('Image generation completed, URL:', response.imageURL)
      
      if (response.imageURL) {
        // Update with generated image
        console.log('Updating database with image URL')
        await databaseService.updatePromptImage(promptData.id, response.imageURL, 'completed')
        
        // Refresh prompt list
        console.log('Refreshing room data')
        await loadRoom()
        
        // Also update local state immediately
        setAllPrompts(prev => prev.map(p => 
          p.id === promptData.id 
            ? { ...p, imageUrl: response.imageURL, imageGenerationStatus: 'completed' } as any 
            : p
        ))
        
        console.log('Image generation process completed successfully')
      } else {
        throw new Error(t('game.errors.noImageUrl') as string)
      }
    } catch (error: any) {
      console.error('Error generating image:', error)
      await databaseService.updatePromptImage(
        promptData.id, 
        '', 
        'failed', 
        error.message || (t('game.errors.imageGenerationFailed') as string)
      )
      
      // Update local state on error too
      setAllPrompts(prev => prev.map(p => 
        p.id === promptData.id 
          ? { ...p, imageGenerationStatus: 'failed' } as any 
          : p
      ))
    }
  }

  const lockMyPrompt = async () => {
    if (!submittedPrompt || locking) return
    setLocking(true)
    try {
      await databaseService.lockPrompt(submittedPrompt.id)
      
      // Play lock sound when prompt is successfully locked
      audioService.playPromptLock()
      
      if (roomId) {
        await realtimeService.broadcast(roomId, 'prompt_locked', {
          promptId: submittedPrompt.id,
          playerId: submittedPrompt.playerId,
        })
      }
      await loadRoom()
    } catch (error: any) {
      setError(error.message || 'Prompt kilitlenemedi')
    } finally {
      setLocking(false)
    }
  }

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60)
    const secs = seconds % 60
    return `${mins}:${secs.toString().padStart(2, '0')}`
  }

  const getPromptStatus = (prompt: Prompt) => {
    switch (prompt.imageGenerationStatus) {
      case 'pending':
        return { text: t('game.status.pending') as string, color: 'text-yellow-600', icon: Clock }
      case 'generating':
        return { text: t('game.status.generating') as string, color: 'text-blue-600', icon: Loader }
      case 'completed':
        return { text: t('game.status.completed') as string, color: 'text-green-600', icon: CheckCircle }
      case 'failed':
        return { text: t('game.status.failed') as string, color: 'text-red-600', icon: CheckCircle }
      default:
        return { text: t('game.status.waiting') as string, color: 'text-gray-600', icon: Clock }
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center space-y-6">
          <div className="relative">
            <div className="w-20 h-20 border-4 border-white/20 border-t-violet-400 rounded-full animate-spin mx-auto"></div>
            <div className="absolute inset-0 w-20 h-20 border-2 border-purple-400/30 rounded-full animate-pulse mx-auto"></div>
            <div className="absolute inset-4 w-12 h-12 bg-gradient-to-r from-violet-500 to-purple-500 rounded-full animate-pulse"></div>
          </div>
          <div className="space-y-3">
            <h3 className="text-2xl font-bold text-gradient">{t('game.loadingTitle')}</h3>
            <p className="text-white/80">{t('game.loadingSubtitle')}</p>
            <div className="flex items-center justify-center space-x-2">
              <Brain className="w-5 h-5 text-violet-400 animate-pulse" />
              <Sparkles className="w-4 h-4 text-purple-400 animate-bounce" />
              <Wand2 className="w-5 h-5 text-pink-400 animate-pulse" />
            </div>
          </div>
        </div>
      </div>
    )
  }

  if (error || !room) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center space-y-6">
          <div className="card-gradient max-w-md mx-auto border-red-400/50 bg-red-500/10">
            <div className="flex items-center justify-center mb-4">
              <div className="w-16 h-16 bg-red-500 rounded-full flex items-center justify-center">
                <Zap className="w-8 h-8 text-white" />
              </div>
            </div>
            <h2 className="text-2xl font-bold text-red-400 mb-4">{t('game.errorTitle')}</h2>
            <p className="text-white/80 mb-6">{error || t('game.errorUnknown')}</p>
            <motion.button
              onClick={() => navigate('/')}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="btn-primary"
            >
              {t('game.backHome')}
            </motion.button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-8">
      {/* NSFW Modal */}
      {showNsfwModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 safe-area">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setShowNsfwModal(false)} />
          <div className="relative w-full max-w-md card p-6 md:p-8">
            <div className="text-center mb-4">
              <div className="w-14 h-14 bg-red-600 rounded-full flex items-center justify-center mx-auto mb-3">
                <span className="text-white text-lg font-bold">!</span>
              </div>
              <h3 className="text-xl font-bold text-white mb-2">{t('game.errorTitle')}</h3>
              <p className="text-white/70 text-sm">{nsfwModalMessage || 'Uygunsuz içerik tespit edildi.'}</p>
            </div>
            <div className="flex space-x-3">
              <button
                onClick={() => {
                  setShowNsfwModal(false)
                  if (isNsfwRetryMode) setNsfwRetryUsed(true)
                }}
                className="flex-1 py-3 px-4 bg-white/10 hover:bg-white/20 text-white font-semibold rounded-xl border border-white/20 transition-all"
              >
                Tamam
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="relative"
      >
        {/* Mobile: back (left), round (center), timer (right) on the same row */}
        <div className="sm:hidden px-4 mb-3">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <motion.button
                onClick={() => navigate(`/lobby/${roomId}`)}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="btn-secondary flex items-center space-x-2"
              >
                <ArrowLeft className="w-4 h-4" />
                <span>{t('common.back')}</span>
              </motion.button>
            </div>
            <div className="flex-1 flex items-center justify-center">
              <motion.div
                initial={{ scale: 0.95, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                className="flex items-center justify-center space-x-2"
              >
                <Target className="w-4 h-4 text-violet-400" />
                <h1 className="text-xl font-bold text-gradient">{t('game.round', { num: room.roundNumber })}</h1>
                <Sparkles className="w-4 h-4 text-purple-400" />
              </motion.div>
            </div>
            <div className="flex-shrink-0">
              <div className={`flex items-center space-x-2 px-3 py-2 rounded-xl backdrop-blur-sm border ${
                timeLeft < 60 
                  ? 'bg-red-500/20 border-red-400/50 text-red-400' 
                  : 'bg-white/10 border-white/20 text-white'
              }`}>
                <Timer className="w-4 h-4" />
                <span className="font-mono text-base font-bold">
                  {formatTime(timeLeft)}
                </span>
              </div>
            </div>
          </div>
          {/* Mobile subtitle under the main row */}
          <div className="mt-1 flex items-center justify-center space-x-2">
            <Brain className="w-3 h-3 text-pink-400" />
            <p className="text-white/80 font-medium text-xs">{t('game.creativityTime')}</p>
            <Wand2 className="w-3 h-3 text-violet-400" />
          </div>
        </div>

        {/* Desktop: back + prompt limit | title | timer */}
        <div className="hidden sm:flex items-center justify-between px-4">
          <div className="flex items-center space-x-4">
            <motion.button
              onClick={() => navigate(`/lobby/${roomId}`)}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="btn-secondary flex items-center space-x-2"
            >
              <ArrowLeft className="w-4 h-4" />
              <span className="hidden sm:inline">{t('lobby.backToLobby')}</span>
            </motion.button>
            {userLimits && (
              <PromptLimitIndicator 
                userLimits={userLimits} 
                className="hidden sm:block"
              />
            )}
          </div>
          
          <div className="text-center flex-1">
            <motion.div
              initial={{ scale: 0.8 }}
              animate={{ scale: 1 }}
              className="flex items-center justify-center space-x-2 mb-1"
            >
              <Target className="w-5 h-5 sm:w-6 sm:h-6 text-violet-400" />
              <h1 className="text-2xl sm:text-3xl font-bold text-gradient">{t('game.round', { num: room.roundNumber })}</h1>
              <Sparkles className="w-5 h-5 sm:w-6 sm:h-6 text-purple-400" />
            </motion.div>
            <div className="flex items-center justify-center space-x-2">
              <Brain className="w-3 h-3 sm:w-4 sm:h-4 text-pink-400" />
              <p className="text-white/80 font-medium text-sm sm:text-base">{t('game.creativityTime')}</p>
              <Wand2 className="w-3 h-3 sm:w-4 sm:h-4 text-violet-400" />
            </div>
          </div>
          
          <div className="hidden sm:flex items-center space-x-3">
            <div className={`flex items-center space-x-2 px-4 py-2 rounded-xl backdrop-blur-sm border ${
              timeLeft < 60 
                ? 'bg-red-500/20 border-red-400/50 text-red-400' 
                : 'bg-white/10 border-white/20 text-white'
            }`}>
              <Timer className="w-5 h-5" />
              <span className="font-mono text-lg font-bold">
                {formatTime(timeLeft)}
              </span>
            </div>
          </div>
        </div>
      </motion.div>

      {/* Current Word */}
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ delay: 0.1 }}
        className="card-gradient text-center relative overflow-hidden"
      >
        {/* Background glow effect */}
        <div className="absolute inset-0 bg-gradient-to-r from-violet-500/10 via-purple-500/10 to-pink-500/10 animate-pulse"></div>
        
        <div className="relative z-10">
          <motion.div
            initial={{ y: -20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.2 }}
            className="flex items-center justify-center space-x-2 sm:space-x-3 mb-4 px-4"
          >
            <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-amber-500 to-orange-600 rounded-2xl flex items-center justify-center">
              <Lightbulb className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
            </div>
            <h2 className="text-lg sm:text-xl font-bold text-white">{t('game.currentWordTitle')}</h2>
          </motion.div>
          
          <motion.div
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ delay: 0.3, type: "spring", stiffness: 100 }}
            className="mb-4 sm:mb-6"
          >
            <div className="relative inline-block px-4">
              <div className="absolute inset-0 bg-gradient-to-r from-violet-400 to-purple-400 rounded-2xl blur-lg opacity-50 animate-pulse"></div>
              <div className="relative bg-gradient-to-r from-violet-500/20 to-purple-500/20 backdrop-blur-sm px-4 py-3 sm:px-6 sm:py-4 lg:px-8 lg:py-4 rounded-2xl border border-violet-400/30">
                <p className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-gradient-gold animate-pulse-glow break-words text-center">
                  {(i18n.language?.startsWith('en') ? (room.currentWordEn || room.currentWord) : (room.currentWordTr || room.currentWord)) || t('game.wordPreparing')}
                </p>
              </div>
            </div>
          </motion.div>
          
          <motion.div
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.4 }}
            className="flex items-center justify-center space-x-2"
          >
            <Sparkles className="w-5 h-5 text-violet-400" />
            <p className="text-white/80 font-medium">
              {(((i18n.language?.startsWith('en') ? (room.currentWordEn || room.currentWord) : (room.currentWordTr || room.currentWord)) || '') as string).toString().length > 0 ? t('game.wordHelpActive') : t('game.wordHelpPreparing')}
            </p>
            <Wand2 className="w-5 h-5 text-purple-400" />
          </motion.div>
        </div>
      </motion.div>

      {/* Prompt Input */}
      {(!submittedPrompt || (isNsfwRetryMode && nsfwBlockedPromptId === submittedPrompt?.id)) ? (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="card-gradient"
        >
          <div className="flex items-center space-x-3 mb-6">
            <div className="w-12 h-12 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-2xl flex items-center justify-center">
              <Brain className="w-6 h-6 text-white" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-white">{t('game.writePromptTitle')}</h2>
              <p className="text-white/60">{t('game.writePromptSubtitle')}</p>
            </div>
          </div>
          
          <div className="space-y-6">
            <div className="relative">
              <textarea
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                placeholder={t('game.promptPlaceholder', { word: (i18n.language?.startsWith('en') ? (room.currentWordEn || room.currentWord) : (room.currentWordTr || room.currentWord)) || '...' })}
                className="w-full h-40 p-4 bg-white/5 backdrop-blur-sm border border-white/20 rounded-xl focus:ring-2 focus:ring-violet-500 focus:border-violet-500 outline-none resize-none text-white placeholder-white/50 transition-all duration-300 hover:bg-white/10 focus:bg-white/10"
                maxLength={500}
              />
              <div className="absolute bottom-3 right-3 bg-white/10 backdrop-blur-sm px-2 py-1 rounded-lg">
                <span className="text-xs text-white/60">{prompt.length}/500</span>
              </div>
            </div>
            
            <div className="-mt-2">
              <p className="text-white/50 text-xs">
                {t('game.translationHint')}
              </p>
            </div>

            <div className="flex justify-end">
              <motion.button
                onClick={submitPrompt}
                disabled={submitting || !prompt.trim() || prompt.length < 5}
                whileHover={{ scale: submitting ? 1 : 1.05 }}
                whileTap={{ scale: submitting ? 1 : 0.95 }}
                className="btn-primary flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed min-w-[140px] justify-center"
              >
                {submitting ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                    <span>{t('game.submitting')}</span>
                  </>
                ) : (
                  <>
                    <Send className="w-5 h-5" />
                    <span>{t('game.submit')}</span>
                  </>
                )}
              </motion.button>
            </div>
          </div>
          
        </motion.div>
      ) : (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="card-gradient bg-gradient-to-r from-green-500/20 to-emerald-500/20 border-green-400/50"
        >
          <div className="flex items-center space-x-4 mb-6">
            <div className="w-14 h-14 bg-gradient-to-br from-green-500 to-emerald-600 rounded-2xl flex items-center justify-center">
              <CheckCircle className="w-8 h-8 text-white" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-white">{t('game.submittedTitle')}</h2>
              <p className="text-white/70">{t('game.submittedSubtitle')}</p>
            </div>
          </div>
          
          <div className="bg-white/10 backdrop-blur-sm p-6 rounded-xl border border-white/20 mb-6">
            <div className="flex items-start space-x-3 mb-3">
              <Brain className="w-5 h-5 text-violet-400 mt-1" />
              <span className="text-violet-300 font-semibold text-sm">{t('game.yourPrompt')}</span>
            </div>
            <p className="text-white font-medium text-lg leading-relaxed">{submittedPrompt.text}</p>
          </div>
          
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center space-x-3">
              {(() => {
                const status = getPromptStatus(submittedPrompt)
                const IconComponent = status.icon
                return (
                  <>
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                      status.text === t('game.status.generating') ? 'bg-blue-500/20' :
                      status.text === t('game.status.completed') ? 'bg-green-500/20' :
                      status.text === t('game.status.failed') ? 'bg-red-500/20' : 'bg-gray-500/20'
                    }`}>
                      <IconComponent className={`w-5 h-5 ${status.color} ${status.text === t('game.status.generating') ? 'animate-spin' : ''}`} />
                    </div>
                    <div>
                      <span className={`font-bold ${status.color}`}>{t('game.aiImageLabel')} {status.text}</span>
                      <div className="text-white/60 text-sm">
                        {status.text === t('game.status.generating') && t('game.statusDetail.generating')}
                        {status.text === t('game.status.completed') && t('game.statusDetail.completed')}
                        {status.text === t('game.status.failed') && t('game.statusDetail.failed')}
                        {status.text === t('game.status.pending') && t('game.statusDetail.pending')}
                      </div>
                    </div>
                  </>
                )
              })()}
            </div>

            {submittedPrompt.imageUrl && (
              <motion.button
                onClick={lockMyPrompt}
                disabled={locking || submittedPrompt.isLocked}
                whileHover={{ scale: submittedPrompt.isLocked ? 1 : 1.05 }}
                whileTap={{ scale: submittedPrompt.isLocked ? 1 : 0.95 }}
                className={`px-6 py-3 rounded-xl font-bold flex items-center space-x-2 transition-all ${
                  submittedPrompt.isLocked 
                    ? 'bg-gray-500/20 text-gray-400 cursor-not-allowed border border-gray-500/30' 
                    : 'bg-gradient-to-r from-violet-600 to-purple-600 text-white hover:from-violet-700 hover:to-purple-700 shadow-lg'
                }`}
              >
                {submittedPrompt.isLocked ? (
                  <>
                    <LockIcon className="w-5 h-5" />
                    <span>{t('game.lock.locked').replace(' ✓','')}</span>
                  </>
                ) : locking ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                    <span>{t('game.lock.locking')}</span>
                  </>
                ) : (
                  <>
                    <UnlockIcon className="w-5 h-5" />
                    <span>{t('game.lock.lockShort')}</span>
                  </>
                )}
              </motion.button>
            )}
          </div>

          {/* Show generated image */}
          {submittedPrompt.imageUrl && (
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="relative overflow-hidden rounded-2xl"
            >
              <div className="absolute inset-0 bg-gradient-to-r from-violet-500/20 to-purple-500/20 rounded-2xl"></div>
              <img 
                src={submittedPrompt.imageUrl} 
                alt="AI image" 
                className="w-full h-64 md:h-80 object-cover rounded-2xl border border-white/20 shadow-2xl" 
              />
              <div className="absolute bottom-3 right-3 flex space-x-2">
                <ReportContentButton roomId={room.id} promptId={submittedPrompt.id} imageUrl={submittedPrompt.imageUrl || undefined} compact />
                <motion.button
                  onClick={() => setShowFullscreenImage(true)}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="w-10 h-10 bg-white/10 backdrop-blur-md rounded-full flex items-center justify-center text-white hover:bg-white/20 transition-colors"
                  title="View fullscreen"
                >
                  <Maximize2 className="w-5 h-5" />
                </motion.button>
              </div>
            </motion.div>
          )}
        </motion.div>
      )}

      {/* Players Progress */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
        className="card"
      >
        <h2 className="text-lg font-semibold mb-4 flex items-center space-x-2">
          <Users className="w-5 h-5 text-primary-600" />
          <span>{t('game.players.title')}</span>
        </h2>
        
        <div className="space-y-2 sm:space-y-3">
          {room.players.map((player) => {
            const playerCurrentRoundPrompt = allPrompts.find(p => p.playerId === player.id && p.roundNumber === room.roundNumber)
            const playerAllPrompts = allPrompts.filter(p => p.playerId === player.id)
            const completedRounds = playerAllPrompts.filter(p => p.isLocked).length
            const hasSubmittedCurrent = !!playerCurrentRoundPrompt
            const status = playerCurrentRoundPrompt ? getPromptStatus(playerCurrentRoundPrompt) : null
            
            return (
              <div
                key={player.id}
                className="flex items-center justify-between p-3 rounded-2xl border backdrop-blur-sm bg-white/5 border-white/10 hover:bg-white/10 transition-colors"
              >
                <div className="flex items-center space-x-3">
                  <div className="w-8 h-8 bg-gradient-to-br from-primary-400 to-secondary-400 rounded-full flex items-center justify-center text-white text-sm font-medium">
                    {player.name.charAt(0).toUpperCase()}
                  </div>
                  <div>
                    <span className="font-medium">{player.name}</span>
                    {player.id === currentPlayer?.id && (
                      <span className="bg-gradient-to-r from-violet-500 to-purple-500 text-white text-xs font-bold px-2 py-1 rounded-full ml-2">
                        {t('game.players.you')}
                      </span>
                    )}
                    <div className="text-xs text-white/60">
                      {t('game.players.roundsCompleted', { completed: completedRounds, total: room.settings.maxRounds })}
                    </div>
                  </div>
                </div>
                
                <div className="flex items-center space-x-2">
                  {hasSubmittedCurrent ? (
                    <>
                      {status && (() => {
                        const IconComponent = status.icon
                        return (
                          <>
                            <IconComponent className={`w-4 h-4 ${status.color} ${status.text === 'Oluşturuluyor' ? 'animate-spin' : ''}`} />
                            <span className={`text-sm ${status.color}`}>
                              {status.text}
                            </span>
                          </>
                        )
                      })()}
                    </>
                  ) : (
                    <span className="text-sm text-white/60">{t('game.players.typing')}</span>
                  )}
                </div>
              </div>
            )
          })}
        </div>
      </motion.div>

      {/* Mobile Prompt Limit Indicator moved under Players Progress */}
      {userLimits && (
        <div className="sm:hidden mt-3">
          <PromptLimitIndicator 
            userLimits={userLimits} 
            showDetails={true}
          />
        </div>
      )}

      {error && (
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="bg-red-50 border border-red-200 rounded-lg p-4"
        >
          <p className="text-red-600 text-center">{error}</p>
        </motion.div>
      )}

      {/* Paywall Modal */}
      <PaywallModal
        isOpen={showPaywall}
        onClose={() => setShowPaywall(false)}
        userLimits={userLimits}
      />

      {/* Fullscreen Image Modal */}
      <FullscreenImageModal
        isOpen={showFullscreenImage}
        onClose={() => setShowFullscreenImage(false)}
        imageUrl={submittedPrompt?.imageUrl || ''}
        imageAlt={`AI generated image for prompt: ${submittedPrompt?.text}`}
        promptText={submittedPrompt?.text}
        playerName={currentPlayer?.name}
        roundNumber={room?.roundNumber}
      />

      {/* Room Closed Modal */}
      <RoomClosedModal
        isOpen={showRoomClosed}
        onClose={() => {
          try {
            if (roomId) {
              realtimeService.cleanupRoom(roomId)
              sessionStorage.removeItem(`joinedBroadcast:${roomId}`)
            }
          } finally {
            navigate('/')
          }
        }}
      />
    </div>
  )
}
